import React, { useState, useEffect } from 'react';
import { creditorsAPI } from '../services/api';
import { formatApiError } from '../utils/errorMessage';
import CreditorLiabilitiesModal from '../features/creditors/CreditorLiabilitiesModal';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error?.name, errorInfo?.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800">
                {this.props.section} Error
              </h3>
              <p className="text-sm text-red-700 mt-1">
                Something went wrong in this section
              </p>
              {this.props.onRetry && (
                <button
                  onClick={() => {
                    this.setState({ hasError: false, error: null });
                    this.props.onRetry();
                  }}
                  className="mt-2 text-sm font-medium text-red-600 hover:text-red-500"
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Creditor Type Badge
const CreditorTypeBadge = ({ type }) => {
  const colors = {
    tax_authority: 'bg-red-100 text-red-800',
    insurance_company: 'bg-blue-100 text-blue-800',
    lender: 'bg-green-100 text-green-800',
    hoa: 'bg-purple-100 text-purple-800',
    utility: 'bg-yellow-100 text-yellow-800',
    vendor: 'bg-gray-100 text-gray-800',
    other: 'bg-gray-100 text-gray-600'
  };
  
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[type] || colors.other}`}>
      {type ? type.replace(/_/g, ' ').toUpperCase() : 'OTHER'}
    </span>
  );
};

// Creditor Row Component
const CreditorRow = ({ creditor, onEdit, onDelete, onViewLiabilities }) => {
  const [rowError, setRowError] = useState(null);
  
  const handleEdit = () => {
    try {
      onEdit(creditor);
    } catch (error) {
      console.error('Error editing creditor:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      setRowError('Failed to open edit form');
    }
  };
  
  const handleDelete = async () => {
    try {
      await onDelete(creditor.id);
    } catch (error) {
      console.error('Error deleting creditor:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      setRowError('Failed to delete creditor');
    }
  };

  const handleViewLiabilities = () => {
    try {
      onViewLiabilities(creditor);
    } catch (error) {
      console.error('Error viewing liabilities:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      setRowError('Failed to view liabilities');
    }
  };
  
  if (rowError) {
    return (
      <tr>
        <td colSpan="7" className="px-6 py-4">
          <div className="text-red-600 text-sm">
            {rowError}
            <button 
              onClick={() => setRowError(null)}
              className="ml-2 text-blue-600 hover:text-blue-800"
            >
              Dismiss
            </button>
          </div>
        </td>
      </tr>
    );
  }
  
  return (
    <ErrorBoundary section="Creditor Row">
      <tr className="hover:bg-gray-50">
        <td className="px-6 py-4">
          <ErrorBoundary section="Creditor Name">
            <div className="font-medium text-gray-900">{creditor.creditor_name}{!creditor.business_id && (<span className="ml-2 text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-800">Shared</span>)}</div>
            {creditor.account_number && (
              <div className="text-sm text-gray-500">Acct: {creditor.account_number}</div>
            )}
          </ErrorBoundary>
        </td>
        <td className="px-6 py-4">
          <ErrorBoundary section="Creditor Type">
            <CreditorTypeBadge type={creditor.creditor_type} />
          </ErrorBoundary>
        </td>
        <td className="px-6 py-4">
          <ErrorBoundary section="Contact Info">
            <div className="text-sm text-gray-900">
              {creditor.contact_phone && (
                <div>📞 {creditor.contact_phone}</div>
              )}
              {creditor.contact_email && (
                <div>✉️ {creditor.contact_email}</div>
              )}
              {creditor.contact_url && (
                <div>
                  <a 
                    href={creditor.contact_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    🔗 Website
                  </a>
                </div>
              )}
              {!creditor.contact_phone && !creditor.contact_email && !creditor.contact_url && (
                <span className="text-gray-400">No contact info</span>
              )}
            </div>
          </ErrorBoundary>
        </td>
        <td className="px-6 py-4 text-center">
          <ErrorBoundary section="Liability Count">
            <span className="text-sm font-medium text-gray-900">
              {creditor.liability_count || 0}
            </span>
          </ErrorBoundary>
        </td>
        <td className="px-6 py-4 text-right">
          <ErrorBoundary section="Total Balance">
            <span className="text-sm font-medium text-gray-900">
              ${parseFloat(creditor.total_balance || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </span>
          </ErrorBoundary>
        </td>
        <td className="px-6 py-4">
          <ErrorBoundary section="Status">
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${creditor.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
              {creditor.is_active ? 'Active' : 'Inactive'}
            </span>
          </ErrorBoundary>
        </td>
        <td className="px-6 py-4 text-right">
          <ErrorBoundary section="Actions">
            <button
              onClick={handleViewLiabilities}
              className="text-indigo-600 hover:text-indigo-900 text-sm mr-3"
            >
              💰 Liabilities
            </button>
            <button
              onClick={handleEdit}
              className="text-indigo-600 hover:text-indigo-900 text-sm mr-3"
            >
              ✏️ Edit
            </button>
            <button
              onClick={handleDelete}
              className="text-red-600 hover:text-red-900 text-sm"
            >
              🗑️ Delete
            </button>
          </ErrorBoundary>
        </td>
      </tr>
    </ErrorBoundary>
  );
};

// Creditor Form Modal
const CreditorFormModal = ({ isOpen, onClose, creditor, onSave }) => {
  const [formData, setFormData] = useState({
    creditor_name: '',
    creditor_type: '',
    contact_phone: '',
    contact_email: '',
    contact_url: '',
    mailing_address: '',
    account_number: '',
    notes: '',
    is_active: true,
    scope: 'profile'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (creditor) {
      setFormData({
        creditor_name: creditor.creditor_name || '',
        creditor_type: creditor.creditor_type || '',
        contact_phone: creditor.contact_phone || '',
        contact_email: creditor.contact_email || '',
        contact_url: creditor.contact_url || '',
        mailing_address: creditor.mailing_address || '',
        account_number: creditor.account_number || '',
        notes: creditor.notes || '',
        is_active: creditor.is_active !== undefined ? creditor.is_active : true,
        scope: creditor.business_id ? 'profile' : 'shared'
      });
    } else {
      setFormData({
        creditor_name: '',
        creditor_type: '',
        contact_phone: '',
        contact_email: '',
        contact_url: '',
        mailing_address: '',
        account_number: '',
        notes: '',
        is_active: true,
        scope: 'profile'
      });
    }
  }, [creditor]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!formData.creditor_name) {
      setError('Creditor name is required');
      return;
    }
    
    setSaving(true);
    try {
      await onSave(formData, creditor?.id);
      onClose();
    } catch (error) {
      console.error('Error saving creditor:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      setError(formatApiError(error, 'Failed to save creditor'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">
            {creditor ? 'Edit Creditor' : 'Add New Creditor'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <ErrorBoundary section="Creditor Form">
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3" role="alert">
                <p className="text-sm text-red-600">{formatApiError(error, 'An error occurred')}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Creditor Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Creditor Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.creditor_name}
                  onChange={(e) => setFormData({ ...formData, creditor_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Scope */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Used by</label>
                <select
                  value={formData.scope}
                  onChange={(e) => setFormData({ ...formData, scope: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="profile">This profile only</option>
                  <option value="shared">All my profiles (shared)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Shared creditors (for example, a bank you use everywhere) appear in every profile.</p>
              </div>

              {/* Creditor Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Creditor Type
                </label>
                <select
                  value={formData.creditor_type}
                  onChange={(e) => setFormData({ ...formData, creditor_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Type</option>
                  <option value="tax_authority">Tax Authority</option>
                  <option value="insurance_company">Insurance Company</option>
                  <option value="lender">Lender</option>
                  <option value="hoa">HOA</option>
                  <option value="utility">Utility</option>
                  <option value="vendor">Vendor</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Account Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Number
                </label>
                <input
                  type="text"
                  value={formData.account_number}
                  onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Contact Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Contact Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Contact URL */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Website/Payment Portal URL
                </label>
                <input
                  type="url"
                  value={formData.contact_url}
                  onChange={(e) => setFormData({ ...formData, contact_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://"
                />
              </div>

              {/* Mailing Address */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mailing Address
                </label>
                <textarea
                  value={formData.mailing_address}
                  onChange={(e) => setFormData({ ...formData, mailing_address: e.target.value })}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Notes */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Active Status */}
              <div className="md:col-span-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </label>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                disabled={saving}
              >
                {saving ? 'Saving...' : (creditor ? 'Update' : 'Create')}
              </button>
            </div>
          </ErrorBoundary>
        </form>
      </div>
    </div>
  );
};

// Main Creditors Component
function Creditors({ selectedBusiness }) {
  const [creditors, setCreditors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showLiabilitiesModal, setShowLiabilitiesModal] = useState(false);
  const [selectedCreditor, setSelectedCreditor] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [filterActive, setFilterActive] = useState('true');

  // Reload creditors when business selection changes
  useEffect(() => {
    loadCreditors();
  }, [selectedBusiness, filterType, filterActive]);

  const loadCreditors = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      // Add business_id filter if a business is selected
      if (selectedBusiness) {
        params.business_id = selectedBusiness;
      }
      if (filterType) params.creditor_type = filterType;
      if (filterActive) params.is_active = filterActive;
      
      const response = await creditorsAPI.getAll(params);
      setCreditors(response.data);
    } catch (error) {
      console.error('Error loading creditors:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      setError('Failed to load creditors');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData, id) => {
    try {
      if (id) {
        // Update existing creditor
        await creditorsAPI.update(id, {
          ...formData,
          ...(formData.scope === 'profile' && selectedBusiness ? { business_id: selectedBusiness } : {})
        });
      } else {
        // Create new creditor - include business_id if selected
        const createData = {
          ...formData,
          business_id: formData.scope === 'shared' ? null : (selectedBusiness || null)
        };
        await creditorsAPI.create(createData);
      }
      await loadCreditors();
    } catch (error) {
      throw error;
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this creditor?')) {
      return;
    }
    
    try {
      await creditorsAPI.delete(id);
      await loadCreditors();
    } catch (error) {
      alert(formatApiError(error, 'Failed to delete creditor'));
      throw error;
    }
  };

  const handleAddNew = () => {
    setSelectedCreditor(null);
    setShowModal(true);
  };

  const handleEdit = (creditor) => {
    setSelectedCreditor(creditor);
    setShowModal(true);
  };

  const handleViewLiabilities = (creditor) => {
    setSelectedCreditor(creditor);
    setShowLiabilitiesModal(true);
  };

  const totalBalance = creditors.reduce((sum, c) => sum + parseFloat(c.total_balance || 0), 0);
  const totalLiabilities = creditors.reduce((sum, c) => sum + parseInt(c.liability_count || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <ErrorBoundary section="Creditors Page">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Creditors Management</h1>
            <p className="text-gray-600 mt-2">
              Manage vendors, lenders, and payees
              {selectedBusiness && (
                <span className="ml-2 text-sm text-blue-600 font-medium">
                  (Filtered by selected business)
                </span>
              )}
              {!selectedBusiness && (
                <span className="ml-2 text-sm text-amber-600 font-medium">
                  (Showing all businesses - select a business to filter)
                </span>
              )}
            </p>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <ErrorBoundary section="Summary Cards">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-sm font-medium text-gray-600">Total Creditors</div>
                <div className="text-3xl font-bold text-gray-900 mt-2">{creditors.length}</div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-sm font-medium text-gray-600">Total Liabilities</div>
                <div className="text-3xl font-bold text-gray-900 mt-2">{totalLiabilities}</div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-sm font-medium text-gray-600">Total Balance Owed</div>
                <div className="text-3xl font-bold text-gray-900 mt-2">
                  ${totalBalance.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </div>
              </div>
            </ErrorBoundary>
          </div>

          {/* Filters and Actions */}
          <div className="bg-white rounded-lg shadow mb-6 p-4">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Types</option>
                  <option value="tax_authority">Tax Authority</option>
                  <option value="insurance_company">Insurance Company</option>
                  <option value="lender">Lender</option>
                  <option value="hoa">HOA</option>
                  <option value="utility">Utility</option>
                  <option value="vendor">Vendor</option>
                  <option value="other">Other</option>
                </select>

                <select
                  value={filterActive}
                  onChange={(e) => setFilterActive(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="true">Active Only</option>
                  <option value="false">Inactive Only</option>
                </select>
              </div>

              <button
                onClick={handleAddNew}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                ➕ Add Creditor
              </button>
            </div>
          </div>

          {/* Creditors Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <ErrorBoundary section="Creditors Table">
              {loading && (
                <div className="text-center py-12">
                  <p className="text-gray-600">Loading creditors...</p>
                </div>
              )}

              {error && (
                <div className="p-6">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
                    <p className="text-sm text-red-600">{formatApiError(error, 'Failed to load creditors')}</p>
                    <button
                      onClick={loadCreditors}
                      className="mt-2 text-sm font-medium text-red-600 hover:text-red-500"
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              )}

              {!loading && !error && creditors.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-600">No creditors found.</p>
                  <button
                    onClick={handleAddNew}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Add Your First Creditor
                  </button>
                </div>
              )}

              {!loading && !error && creditors.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Liabilities
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Owed
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {creditors.map((creditor) => (
                        <CreditorRow
                          key={creditor.id}
                          creditor={creditor}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                          onViewLiabilities={handleViewLiabilities}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </ErrorBoundary>
          </div>
        </div>

        {/* Modals */}
        <CreditorFormModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          creditor={selectedCreditor}
          onSave={handleSave}
        />

        <CreditorLiabilitiesModal selectedBusiness={selectedBusiness}
          isOpen={showLiabilitiesModal}
          onClose={() => setShowLiabilitiesModal(false)}
          creditor={selectedCreditor}
        />
      </ErrorBoundary>
    </div>
  );
}

export default Creditors;
